import * as React from 'react';
import { resolve } from 'inversify-react';
import { Button } from 'antd';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';
import { Link } from 'react-router-dom';
import { connect } from "react-redux";

import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { FormatService } from 'src/core/services/format.service';
import { BreakdownList } from 'src/app/accounting/suppliers/cost/parts/breakdown-list';
import { TextView } from 'src/core/ui/text.view';
import { ObjectUtils } from 'src/core/utils/object';
import { PriceAdjustmentModal, AdjustmentModel } from 'src/app/accounting/shared/price-adjustment/price-adjustment-modal';
import { SummaryTableHeader } from 'src/app/accounting/shared/summary-table-header';

interface PropsFromState {
	currencies: CurrencySummary[];
}

interface Props extends ComponentViewProps<DataSet<QuoteEntrySummary>> {
	payloadDataSet?: DataSet<SelectableInvoiceEntryPayload>;
	onChangeSelection?: (selectdQuoteEntryIds: string[]) => void;
	onAdjustSale: (saleAdjustment: SalesAdjustmentProductManually) => void;
}

interface State {
	adjustmentModel?: AdjustmentModel;
}

@observer
class QuoteEntryList extends React.Component<Props & PropsFromState, State> {
	@resolve
	private formatService: FormatService;

	public state: State = {
		adjustmentModel: null,
	};

	private getCurrencyFormat(currencyCode: string): string {
		const currency = this.props.currencies.find(e => e.iso3Name === currencyCode);

		return currency.format;
	}

	private renderCurrency(value: number, currencyCode?: string): string {
		const currencyFormat = currencyCode != null ? this.getCurrencyFormat(currencyCode) : '${0}';

		return this.formatService.toString('currency', ObjectUtils.round(value, 2), currencyFormat);
	}

	private getCheckedItemsSum(selector: (value: QuoteEntrySummary) => number): string {
		const { model, payloadDataSet } = this.props;

		const sum = model.items
			.filter(m => (payloadDataSet.items.find(p => p.quoteEntryId === m.id) || { checked: false }).checked)
			.reduce((prev, curr) => prev + selector(curr), 0);

		return this.renderCurrency(sum, model.items.length > 0 ? model.items[0].costCurrencyCode : null);
	}
	private getCostSum = (): string => this.getCheckedItemsSum((entry) => entry.costPrice);
	private getInvoicedSum = (): string => this.getCheckedItemsSum((entry) => entry.invoicedCost);
	private getAccrualSum = (): string => this.getCheckedItemsSum((entry) => entry.accrualValue);

	private renderItemsPerLine(items: any[]): React.ReactNode {
		return items.map((m, index) => (
			<React.Fragment key={index}>
				{index > 0 && <br />}
				{m}
			</React.Fragment>
		));
	}

	@autobind
	private handleShowAdjustmentModal(entry: QuoteEntrySummary): void {
		this.setState({
			adjustmentModel: {
				...entry.entity.initData,
				costPrice: entry.costPrice,
				sellPrice: entry.sellPrice,
				salePriceGross: entry.salePriceGross,
				calculatedCostPrice: entry.calculatedCostPrice,
				calculatedSalePriceGross: entry.calculatedSalePriceGross,
				sellExchangeRate: entry.sellExchangeRate,
				salesmanProfit: entry.salesmanProfit,
				quoteEntryId: entry.id,
				occupancies: entry.occupancy
			}
		});
	}

	@autobind
	private async handleCloseAdjustmentModal(salesAdjustment?: SalesAdjustmentProductManually): Promise<void> {
		this.setState({ adjustmentModel: null });
		const { onAdjustSale } = this.props;

		if (salesAdjustment) {
			onAdjustSale(salesAdjustment);
		}
	}

	@autobind
	private handleExpandedRowRender(entry: QuoteEntrySummary): React.ReactNode {
		return (
			<BreakdownList
				quoteEntryId={entry.id}
				currencyFormat={this.getCurrencyFormat(entry.costCurrencyCode)}
				setBusy={this.props.setBusy}
			/>
		);
	}

	@autobind
	private getCheckboxProps(item: QuoteEntrySummary): Object {
		return {
			disabled: item.costPrice === item.invoicedCost || (item.bookingStatus !== 'Confirmed' && item.bookingStatus !== 'Open')
		};
	}

	render(): React.ReactNode {
		const { model, setBusy, onAdjustSale, payloadDataSet, onChangeSelection } = this.props;

		const selectedRowKeys = model.items
			.filter(m => (payloadDataSet.items.find(p => p.quoteEntryId === m.id) || { checked: false }).checked)
			.map(m => m.id);

		const columns: Column[] = [];
		if (onAdjustSale != null) {
			columns.push({
				title: 'Adjustment',
				key: 'adjustment',
				width: 125,
				render: (_, record: QuoteEntrySummary) => (
					<Button
						icon="pound"
						onClick={() => this.handleShowAdjustmentModal(record)}>
						Adjust
					</Button>
				)
			});
		}

		columns.push(
			{
				title: 'Supplier',
				key: 'supplier',
				dataIndex: 'supplierTitle',
				width: 150
			},
			{
				title: 'Booking Reference',
				key: 'bookingReference',
				dataIndex: 'bookingReference',
				render: (text, record: QuoteEntrySummary) => <Link to={`/bookings/quotes/${record.quoteId}`}>{text}</Link>,
				width: 180
			},
			{
				title: 'Confirmation Reference',
				key: 'confirmationReferenceNumber',
				dataIndex: 'confirmationReferenceNumber',
				width: 180
			},
			{
				title: 'Product',
				key: 'product',
				dataIndex: 'productDisplayName',
				width: 200,
			},
			{
				title: 'Service Date',
				key: 'dates',
				children: [
					{
						key: 'dateFrom',
						dataIndex: 'dateFrom',
						title: 'From',
						width: 150,
						render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
					},
					{
						key: 'dateTo',
						dataIndex: 'dateTo',
						title: 'To',
						width: 150,
						render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
					}
				]
			},
			{
				title: 'Cost',
				key: 'costPrice',
				dataIndex: 'costPrice',
				width: 75,
				render: (field: number, record: QuoteEntrySummary) => this.renderCurrency(field, record.costCurrencyCode)
			},
			{
				title: 'Invoiced',
				key: 'invoicedCost',
				dataIndex: 'invoicedCost',
				width: 80,
				render: (field: number, record: QuoteEntrySummary) => this.renderCurrency(field, record.costCurrencyCode)
			},
			{
				title: 'Accrual Value',
				key: 'accrualValue',
				dataIndex: 'accrualValue',
				width: 110,
				render: (field: number, record: QuoteEntrySummary) => this.renderCurrency(field, record.costCurrencyCode)
			},
			{
				title: 'Amount',
				key: 'amount',
				width: 125,
				editable: (_, record: QuoteEntrySummary) => {
					const payload = payloadDataSet.items.find(m => m.quoteEntryId === record.id);
					if (payload == null) {
						return null;
					}

					return (
						<TextView
							label={null}
							mode={payload.checked ? 'edit' : 'display'}
							value={payload.cost}
							onChanged={(value) => {
								payload.cost = value;
							}}
							targetType="float+"
							max={record.accrualValue}
							suffix={record.costCurrencyCode}
						/>
					);
				}
			},
			{
				title: 'Comments',
				key: 'comments',
				width: 200,
				editable: (_, record: QuoteEntrySummary) => {
					const payload = payloadDataSet.items.find(m => m.quoteEntryId === record.id);
					if (payload == null) {
						return null;
					}

					return (
						<TextView
							label={null}
							rows={3}
							mode={payload.checked ? 'edit' : 'display'}
							value={payload.comments}
							onChanged={(value) => {
								payload.comments = value;
							}}
						/>
					);
				}
			}
		);

		columns.push(
			{
				title: 'Clients',
				key: 'clientsDisplayNames',
				dataIndex: 'clientsDisplayNames',
				width: 100,
				render: (field: string) => this.renderItemsPerLine(field.split(','))
			},
			{
				title: 'Booking Status',
				key: 'bookingStatus',
				dataIndex: 'bookingStatus',
				width: 150
			},
			{
				title: 'Client/Unit',
				key: 'occupancyText',
				dataIndex: 'occupancyText',
				width: 100
			},
			{
				title: 'Reservation Date',
				key: 'reservationDate',
				dataIndex: 'reservationDate',
				width: 150,
				render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
			}
		);

		const { adjustmentModel } = this.state;

		return (
			<React.Fragment>
				{
					adjustmentModel &&
					<PriceAdjustmentModal
						model={adjustmentModel}
						costCurrencyFormat={this.getCurrencyFormat(adjustmentModel.costCurrencyCode)}
						sellCurrencyFormat={this.getCurrencyFormat(adjustmentModel.sellCurrencyCode)}
						onOk={this.handleCloseAdjustmentModal}
						onCancel={this.handleCloseAdjustmentModal}
						setBusy={setBusy}
					/>
				}
				{
					onChangeSelection &&
					<SummaryTableHeader
						fields={[
							{ label: 'Cost', value: this.getCostSum() },
							{ label: 'Invoiced', value: this.getInvoicedSum() },
							{ label: 'Accrual', value: this.getAccrualSum() }
						]}
					/>
				}
				<DataTableView
					mode="display"
					rowKey={(record) => record.id}
					model={model}
					columns={columns}
					setBusy={this.props.setBusy}
					expandedRowRender={this.handleExpandedRowRender}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
					selection={onChangeSelection != null
						? {
							selectedRowKeys: selectedRowKeys,
							getCheckboxProps: this.getCheckboxProps,
							onChange: onChangeSelection
						}
						: false
					}
					otherProps={{
						className: 'break-words',
						scroll: { x: 2350, y: 600 }
					}}
				/>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: IApplicationState): PropsFromState => {
	return {
		currencies: state.currencies,
	};
};

const connected = connect(
	mapStateToProps,
)(QuoteEntryList);
export { connected as QuoteEntryList };



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/cost/parts/quote-entry-list.tsx